"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { encryptId } from "../../utils/crypto";
import { motion } from "framer-motion";

export default function ProfessionalsSection({ searchQuery }) { 
  const [professionals, setProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);

  useEffect(() => {
    async function fetchProfessionals() {
      try {
        const response = await axios.get("http://localhost:5000/professionals");
        const professionals = response.data.map((professional) => ({
          ...professional,
          image: professional.personal_image
            ? decodeImage(professional.personal_image)
            : "",
        }));
        setProfessionals(professionals);
        setFilteredProfessionals(professionals);
      } catch (error) {
        console.error("Error fetching professionals:", error);
      }
    }
    fetchProfessionals();
  }, []);

  const decodeImage = (base64String) => {
    return `data:image/jpeg;base64,${base64String}`;
  };

  useEffect(() => {
    if (!searchQuery) {
      setFilteredProfessionals(professionals);
    } else {
      setFilteredProfessionals(
        professionals.filter(
          (professional) =>
            (professional.name && professional.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (professional.role && professional.role.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      );
    }
  }, [searchQuery, professionals]);

  return (
    <section style={{ marginBottom: '64px' }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <div style={{
          width: '4px',
          height: '32px',
          borderRadius: '2px',
          background: 'linear-gradient(180deg, var(--accent-cyan), var(--accent-violet))',
        }} />
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
        }}>Professional Profiles</h2>
        <div style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          background: 'rgba(var(--accent-primary-rgb), 0.08)',
          border: '1px solid rgba(var(--accent-primary-rgb), 0.15)',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--accent-cyan)',
        }}>
          {filteredProfessionals.length}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredProfessionals.map((professional, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
          >
            <Link 
              to={`/page/${encodeURIComponent(encryptId(professional.user_id))}`}
              state={{ profile: professional.profile }}
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-glass)',
                borderRadius: '20px',
                padding: '32px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.3)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(var(--accent-primary-rgb), 0.08)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'var(--border-glass)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                {/* Subtle gradient accent on top */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, var(--accent-cyan), var(--accent-violet), transparent)',
                  opacity: 0.5,
                }} />

                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: '2px solid rgba(var(--accent-primary-rgb), 0.2)',
                  padding: '3px',
                  marginBottom: '16px',
                }}>
                  <img
                    src={professional.image}
                    alt={professional.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: '4px',
                }}>{professional.name}</h3>
                <p style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-muted)',
                }}>{professional.role}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

